import { ExperimentNames } from 'common/experiments/ExperimentDefinitions';
import { supportsMealPlans } from 'common/MealPlanUtils';
import { SessionStorageKeys, StorageKeys } from 'common/storage/constants';
import { QueryParams } from 'common/urls/QueryParams';
import { isNil, omitBy } from 'lodash';
import QueryString, { StringifiableRecord } from 'query-string';
import { matchPath } from 'react-router';
import { useExperiment } from 'src/shared/hooks/experiments/useExperiment';
import { useCurrentUser } from 'src/shared/hooks/useCurrentUserHook';
import { useHistory } from 'src/shared/hooks/useHistory';
import { Routes } from 'src/shared/Routes';
import { useLocalStorage, useSessionStorage } from 'src/shared/storage/Storage';
import { getFullRelativeUrl } from 'src/shared/utils/RouteUtilities';
import { useCachedZipCode } from 'src/user-preferences/useCachedZipCode';
import { useMealPlanIntakeStoreSelectors } from 'src/ztore/meal-plan-intake-store';
import { useMealPlanForUserStoreSelectors } from 'src/ztore/wss/meal-plan-for-user-store';
import { match, P } from 'ts-pattern';
import { StringParam, useQueryParams, withDefault } from 'use-query-params';
import { MealPlanEntryPoints } from '../consts';
import { STEP_1, STEP_HOUSEHOLD, STEP_LOADING } from '../meal-plan-intake/preferences/consts';

type GetMealPlanUrlParams = {
  skipSplashScreen?: boolean;
  entryPoint?: MealPlanEntryPoints;
  step?: string;
};

type ResolveMealPlanUrlInput = {
  props: GetMealPlanUrlParams;
  userHasActiveMealPlan: boolean;
  userHasCompletedIntakeForm: boolean;
  isIntakeOptimizationTreatment: boolean;
  hasValidCheckoutState: boolean;
  currentEntryPoint: string | undefined;
  currentCategory: string | undefined;
};

const formatUrl = (url: string, query: StringifiableRecord) => QueryString.stringifyUrl({ url, query });

/**
 * Prefix-matches every /meal-plans route (intake, checkout, edit, manage, …),
 * unlike `locationInRoutes`, which matches exact routes only.
 */
export const isOnMealPlanRoute = (pathname: string): boolean =>
  !!matchPath(pathname, { path: Routes.CONSUMER_MEAL_PLANS });

/**
 * Resolves the canonical meal-plan URL for a navigation intent.
 *
 * Returns the checkout route only when `hasValidCheckoutState` is true —
 * i.e., the intake store's `mealPlanType` and the user's `cachedZipCode`
 * are both present. `MealPlanCheckoutWrapper` rejects the page and redirects
 * via this hook when either is missing, so returning the checkout route in
 * that state would loop on the same URL and paint a blank page.
 */
export const resolveMealPlanUrl = ({
  props,
  userHasActiveMealPlan,
  userHasCompletedIntakeForm,
  isIntakeOptimizationTreatment,
  hasValidCheckoutState,
  currentEntryPoint,
  currentCategory,
}: ResolveMealPlanUrlInput): string => {
  const { skipSplashScreen, step } = props;
  if (userHasActiveMealPlan) return Routes.CONSUMER_MEAL_PLANS_UPCOMING_ORDER;

  if (userHasCompletedIntakeForm && hasValidCheckoutState) {
    return formatUrl(Routes.CONSUMER_MEAL_PLANS_CHECKOUT, omitBy({ entryPoint: currentEntryPoint }, isNil));
  }

  const resolvedStep = match({ step, skipSplashScreen, userHasCompletedIntakeForm })
    .with({ step: P.when((val) => !isNil(val)) }, () => step)
    .with({ skipSplashScreen: true, userHasCompletedIntakeForm: true }, () => STEP_HOUSEHOLD)
    .otherwise(() => STEP_1);

  // Treatment has no splash step and always opens intake on the loading screen,
  // so both entry steps map to loading; deep links to any other step pass through.
  const intakeStep =
    isIntakeOptimizationTreatment && (resolvedStep === STEP_1 || resolvedStep === STEP_HOUSEHOLD)
      ? STEP_LOADING
      : resolvedStep;

  const entryPointToUse = props.entryPoint ?? currentEntryPoint;
  return formatUrl(
    Routes.CONSUMER_MEAL_PLANS_PREFERENCES,
    omitBy({ entryPoint: entryPointToUse, step: intakeStep, category: currentCategory }, isNil)
  );
};

interface MealPlanSurfacingLogicReturn {
  isInMealPlanRegion: boolean;
  mealPlanHasBeenRedirected: boolean;
  navigateToMealPlanPreferences: (params: GetMealPlanUrlParams) => void;
  setHasSeenMealPlanModal: () => void;
  setMealPlanHasBeenRedirected: () => void;
  shefLogoDisabled: boolean;
  shouldShowMealPlanBanner: boolean;
  shouldShowMealPlanButton: boolean;
  shouldShowMealPlanModal: boolean;
  getMealPlanUrl: (params: GetMealPlanUrlParams) => string;
  visitedMealPlans: boolean;
  setVisitedMealPlans: (value: boolean) => void;
  intakeCompletedAtDate?: string;
}

export const useMealPlanSurfacingLogic = (): MealPlanSurfacingLogicReturn => {
  const [currentUser, currentUserLoading] = useCurrentUser();
  const userHasActiveMealPlan = useMealPlanForUserStoreSelectors.use.userHasActiveMealPlan();
  const history = useHistory();
  const { location } = history;
  const userIsLoggedIn = !isNil(currentUser) && !currentUserLoading;
  const [cachedZipCode, , zipCodeMetaData] = useCachedZipCode();
  const regionId = zipCodeMetaData?.cachedZipCodeData?.zipCode?.region?.id;
  const isMealPlanEnabledRegion = supportsMealPlans({
    regionId,
    deliveryHubId: zipCodeMetaData?.cachedZipCodeData?.zipCode?.deliveryHubId,
  });
  const { getFormData } = useMealPlanIntakeStoreSelectors();
  const { intakeCompletedAtDate, mealPlanType } = getFormData();
  const userHasCompletedIntakeForm = !isNil(intakeCompletedAtDate);
  const hasValidCheckoutState = !isNil(mealPlanType) && !isNil(cachedZipCode);
  const { isTreatment: isIntakeOptimizationTreatment } = useExperiment(ExperimentNames.INTAKE_OPTIMIZATION);

  const [{ entryPoint, category }] = useQueryParams({
    entryPoint: withDefault(StringParam, undefined),
    [QueryParams.CATEGORY]: withDefault(StringParam, undefined),
  });

  const [visitedMealPlans, setVisitedMealPlans] = useLocalStorage<boolean>(StorageKeys.VISITED_MEAL_PLANS, false);
  const [hasSeenMealPlanModalLifetime, setHasSeenMealPlanModalLifetime] = useLocalStorage<boolean>(
    StorageKeys.USER_HAS_SEEN_EXPLORE_MEAL_PLAN_PROPOSITION_MODAL,
    false
  );
  const [hasSeenMealPlanModalSession, setHasSeenMealPlanModalSession] = useSessionStorage<boolean>(
    SessionStorageKeys.USER_HAS_SEEN_EXPLORE_MEAL_PLAN_PROPOSITION_MODAL,
    false
  );
  const [mealPlanHasBeenRedirected, setMealPlanHasBeenRedirectedSession] = useSessionStorage<boolean>(
    SessionStorageKeys.MEAL_PLAN_HAS_BEEN_REDIRECTED,
    false
  );

  const setHasSeenMealPlanModal = () => {
    setHasSeenMealPlanModalLifetime(true);
    setHasSeenMealPlanModalSession(true);
  };

  const setMealPlanHasBeenRedirected = () => {
    setMealPlanHasBeenRedirectedSession(true);
  };

  const shefLogoDisabled = isOnMealPlanRoute(location.pathname);

  const getMealPlanUrl = (props: GetMealPlanUrlParams): string =>
    resolveMealPlanUrl({
      props,
      userHasActiveMealPlan,
      userHasCompletedIntakeForm,
      isIntakeOptimizationTreatment,
      hasValidCheckoutState,
      currentEntryPoint: entryPoint,
      currentCategory: category,
    });

  const navigateToMealPlanPreferences = (props: GetMealPlanUrlParams) => {
    const from = getFullRelativeUrl(history.location);
    const url = getMealPlanUrl(props);

    history.push(url, { from });
  };

  /**
   * Determine if the user should see the persistent meal plan banner on Explore
   */
  const shouldShowMealPlanBanner: boolean = match({
    visitedMealPlans,
    isInMealPlanRegion: isMealPlanEnabledRegion,
  })
    .with({ isInMealPlanRegion: false }, () => false)
    .otherwise(() => true);

  /**
   * Determine if the user should see the meal plan modal on Explore
   */
  const isShef = !!currentUser?.shefProfile;
  const shouldShowMealPlanModal: boolean = match({
    userIsLoggedIn,
    visitedMealPlans,
    hasSeenMealPlanModalLifetime,
    hasSeenMealPlanModalSession,
    isInMealPlanRegion: isMealPlanEnabledRegion,
    userHasActiveMealPlan,
    isShef,
  })
    .with({ isShef: true }, () => false)
    .with({ isInMealPlanRegion: false }, () => false)
    .with({ userHasActiveMealPlan: true }, () => false)
    .with({ userIsLoggedIn: true, visitedMealPlans: true, hasSeenMealPlanModalLifetime: true }, () => false)
    .with({ userIsLoggedIn: true, visitedMealPlans: false, hasSeenMealPlanModalSession: true }, () => false)
    .with({ userIsLoggedIn: false, visitedMealPlans: true, hasSeenMealPlanModalLifetime: true }, () => false)
    .with({ userIsLoggedIn: false, visitedMealPlans: false, hasSeenMealPlanModalSession: true }, () => false)
    .otherwise(() => true);

  /**
   * Determine whether the Meal Plan button should be shown in the hamburger menu
   */
  const shouldShowMealPlanButton = match({
    userHasActiveMealPlan,
    visitedMealPlans,
    regionId,
    isInMealPlanRegion: isMealPlanEnabledRegion,
  })
    .with({ userHasActiveMealPlan: true }, () => true)
    .with(
      P.when((val) => !val.isInMealPlanRegion && val.regionId !== undefined),
      () => false
    )
    .otherwise(() => true);

  return {
    isInMealPlanRegion: isMealPlanEnabledRegion,
    mealPlanHasBeenRedirected,

    // handle navigating user to meal plan urls
    getMealPlanUrl,
    navigateToMealPlanPreferences,

    setHasSeenMealPlanModal,
    setMealPlanHasBeenRedirected,
    shefLogoDisabled,
    shouldShowMealPlanBanner,
    shouldShowMealPlanButton,
    shouldShowMealPlanModal,
    visitedMealPlans,
    setVisitedMealPlans,
    intakeCompletedAtDate,
  };
};
